import type { Screen } from '../types';

const STORAGE_KEY = 'miniOverlayForm';

export interface MiniOverlayFormData {
  screen: Screen;
  fields: {
    email?: string;
    phone_number?: string;
    country_code?: string;
    customFields?: Record<string, string>;
  };
}

let storageAvailable: boolean | null = null;
export function isStorageAvailable(): boolean {
  if (storageAvailable !== null) return storageAvailable;
  try {
    const testKey = '__storage_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    storageAvailable = true;
  } catch (e) {
    storageAvailable = false;
  }
  return storageAvailable;
}

function getOrCreateData(screen: Screen): MiniOverlayFormData {
  const stored = sessionStorage.getItem(STORAGE_KEY);
  if (stored) {
    try {
      const data: MiniOverlayFormData = JSON.parse(stored);
      if (data.screen === screen) return data;
    } catch (e) {
      console.warn('Failed to parse stored form data:', e);
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }
  return { screen, fields: {} };
}

function handleStorageError(e: unknown): void {
  if (e instanceof Error && e.name === 'QuotaExceededError') {
    console.warn('Session storage quota exceeded. Form data not persisted.');
  } else {
    console.warn('Failed to update stored field:', e);
  }
}

export function getStoredFormData(screen: Screen): MiniOverlayFormData['fields'] | null {
  if (!isStorageAvailable()) {
    return null;
  }

  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const data: MiniOverlayFormData = JSON.parse(stored);
    return data.screen === screen ? data.fields : null;
  } catch (e) {
    console.warn('Failed to retrieve stored form data:', e);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
    return null;
  }
}

export function updateStoredField(
  screen: Screen,
  fieldName: 'email' | 'phone_number' | 'country_code',
  value: string,
): void {
  if (!isStorageAvailable()) return;
  try {
    const data = getOrCreateData(screen);
    data.fields[fieldName] = value;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    handleStorageError(e);
  }
}

export function updateStoredCustomField(screen: Screen, fieldId: string, value: string): void {
  if (!isStorageAvailable()) return;
  try {
    const data = getOrCreateData(screen);
    (data.fields.customFields ??= {})[fieldId] = value;
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    handleStorageError(e);
  }
}

export function clearFormStorage(): void {
  if (!isStorageAvailable()) return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    console.warn('Failed to clear form storage:', e);
  }
}
